import * as React from 'react'
import { Link, useHistory } from 'react-router-dom'
import { useQuery } from 'react-query'
import { getFaqList } from '@feedback/services'
import SearchInput from '@feedback/components/SearchInput'
import { IQuestionList } from '@feedback/pages/allfb/questionIndex'
import {
  IQuestionRecommend,
  getQuestionRecommend
} from '@feedback/pages/allfb/questionDetail'
import Highlighter from 'react-highlight-words'
import { Icon } from 'antd-mobile'
import { getTextByKey, reportFn, tracking } from '@feedback/utils/util'
import styles from './index.module.scss'
import { getAppConfig } from '@feedback/utils/CONST'
import { isVestAppid } from '@feedback/utils/siteConfig'
import clsx from 'clsx'

const backIcon: string = require('@feedback/assets/vest/icons/back.svg')
const searchEmptyImg: string = require('@feedback/assets/vest/icons/search-empty.png')

export const SearchQuestionList = async (
  keyWord: string
): Promise<IQuestionList[]> => {
  const { code, data, message } = (await getFaqList({ search: keyWord })) || {}

  if (code !== 0) {
    return Promise.reject(new Error(message))
  }

  return data
}

const SearchResultList: React.FC<{ keyWord: string; isVest?: boolean }> = ({
  keyWord,
  isVest
}) => {
  const history = useHistory()
  const { appid } = getPageConfig()

  const questionList = useQuery(['SearchQuestionList', keyWord], () => {
    // 埋点：有效搜索、关键词
    if (keyWord && appid === 'cm1006') {
      tracking({
        _step_id: 1,
        _feedback_page_id: '',
        _postion_id: '',
        _search_words: keyWord,
        _faq_id: '',
        _src_faq_id: ''
      })
    } else if (keyWord) {
      reportFn({
        parm1: 1,
        parm5: 'h5_feedback_click_2',
        parm6: keyWord
      })
    }
    return SearchQuestionList(keyWord)
  })

  if (questionList.isLoading) {
    return (
      <div className={'faq-list__more'}>
        <Icon type='loading' />
      </div>
    )
  }

  if (!Array.isArray(questionList.data) || questionList.data.length < 1) {
    if (isVest) {
      return (
        <section className={styles.emptyState} aria-label='No search results'>
          <img src={searchEmptyImg} alt='' />
          <p>
            {getTextByKey('No results for')}“
            <span className={styles.emptyQuery}>{keyWord}</span>”
          </p>
          <button
            type='button'
            className={styles.emptyContact}
            onClick={() => {
              reportFn({
                parm1: 3,
                parm3: 1,
                parm5: 'h5_feedback_click_2',
                parm9: keyWord
              })
              history.push('/ticketForm')
            }}
          >
            {getTextByKey('Contact Us')}
          </button>
        </section>
      )
    }
    return (
      <div className={clsx(styles['no-more'])}>
        {getTextByKey('No results for')}“<mark>{keyWord}</mark>”
      </div>
    )
  }

  const dataLength = questionList?.data?.reduce((counter, item) => {
    return counter + item.children.length
  }, 0)

  // 马甲站：扁平结果列表（对照原型 search-result-list），逻辑仍按类别分组数据
  if (isVest) {
    const flatItems: { _id: string; question: string }[] = []
    questionList.data?.forEach((category) => {
      category.children?.forEach((item) => {
        flatItems.push({ _id: item._id, question: item.question })
      })
    })

    return (
      <section className={styles.vestResults} aria-label='Search results'>
        <p className={styles.vestResultCount}>
          {getTextByKey('Found {value} results').replace(
            '{value}',
            `${dataLength || '0'}`
          )}
        </p>
        <div className={styles.vestResultList}>
          {flatItems.map((item) => (
            <button
              type='button'
              className={styles.vestResultItem}
              key={item._id}
              onClick={() => {
                reportFn({
                  parm1: 2,
                  parm2: 1,
                  parm5: 'h5_feedback_click_2',
                  parm7: item._id,
                  parm8: keyWord
                })
                history.push(`/questionDetail?faq_id=${item._id}`)
              }}
            >
              <span>
                <Highlighter
                  highlightClassName={clsx(styles.highlight)}
                  searchWords={[keyWord]}
                  autoEscape={true}
                  textToHighlight={item.question}
                />
              </span>
              <i className={styles.chevron} aria-hidden='true' />
            </button>
          ))}
        </div>
      </section>
    )
  }

  return (
    <section className={styles.list}>
      <label className={styles.tips}>
        {getTextByKey('Found {value} results').replace(
          '{value}',
          `${dataLength || '0'}`
        )}
      </label>
      {questionList.data?.map((category, index) => (
        <div className={styles.list__group} key={index}>
          <h2>
            <Highlighter
              highlightClassName={clsx(styles.highlight)}
              searchWords={[keyWord]}
              autoEscape={true}
              textToHighlight={category.faq_type}
            />
          </h2>
          <ul>
            {category.children.map((item, idx) => (
              <li
                key={idx}
                onClick={() => {
                  // 埋点：搜索页面结果列表 FAQ 点击

                  if (appid === 'cm1006') {
                    tracking({
                      _step_id: 2,
                      _feedback_page_id: '',
                      _postion_id: 1,
                      _search_words: keyWord,
                      _faq_id: item._id,
                      _src_faq_id: ''
                    })
                  } else {
                    reportFn({
                      parm1: 2,
                      parm2: 1,
                      parm5: 'h5_feedback_click_2',
                      parm7: item._id,
                      parm8: keyWord
                    })
                  }
                  history.push(`/questionDetail?faq_id=${item._id}`)
                }}
              >
                <Highlighter
                  highlightClassName={clsx(styles.highlight)}
                  searchWords={[keyWord]}
                  autoEscape={true}
                  textToHighlight={item.question}
                />
              </li>
            ))}
          </ul>
        </div>
      ))}
    </section>
  )
}

const RelaterList: React.FC<{ keyWord?: string }> = ({ keyWord }) => {
  const history = useHistory()
  const { appid } = getAppConfig() || {}
  const relater = useQuery<IQuestionRecommend[], Error>(
    'getQuestionRecommend',
    () => getQuestionRecommend()
  )

  if (relater.isLoading) {
    return (
      <div className={'faq-list__more'}>
        <Icon type='loading' />
      </div>
    )
  }

  if (!Array.isArray(relater.data) || relater.data.length < 1) {
    return <div className={styles['no-more']}>{getTextByKey('No data')}</div>
  }

  return (
    <section className={styles.relater}>
      <label className={styles.tips}>{getTextByKey('Related results')}</label>
      <ul>
        {relater.data?.map((item) => (
          <li
            key={item._id}
            onClick={() => {
              // 埋点：搜索页面推荐列表 FAQ 点击
              if (appid === 'cm1006') {
                tracking({
                  _step_id: 2,
                  _feedback_page_id: '',
                  _postion_id: 2,
                  _search_words: keyWord,
                  _faq_id: item._id,
                  _src_faq_id: ''
                })
              } else {
                reportFn({
                  parm1: 2,
                  parm2: 2,
                  parm5: 'h5_feedback_click_2',
                  parm7: item._id,
                  parm8: keyWord
                })
              }
              history.push(`/questionDetail?faq_id=${item._id}`)
            }}
          >
            {item.question}
          </li>
        ))}
      </ul>
    </section>
  )
}

const getPageConfig = () => {
  const { appid, logo, logoShadow, bgSearch } = getAppConfig() || {}
  return {
    ...(appid === 'cm1001' && { logo }),
    ...(appid === 'cm1002' && { logo }),
    ...(appid === 'cm1003' && { logo }),
    ...(appid === 'cm1006' && { logo }),
    ...(appid === 'cm1007' && { logo }),
    ...(appid === 'cm1008' && { logo: logoShadow }),
    ...(appid === 'cm1009' && { logo }),
    ...(appid === 'cm1017' && { logo }),
    ...(appid === 'cm1011' && { logo: logoShadow }),
    ...(appid === 'cm1012' && { logo }),
    ...(appid === 'cm1015' && { logo }),
    ...(appid === 'cm3002' && { logo }),
    ...(appid === 'cm1013' && { logo }),
    ...(appid === 'cm1018' && { logo }),
    appid,
    bgSearch
  }
}

const SearchResult: React.FC = () => {
  const history = useHistory()
  let { keyWord } = Object.fromEntries(
    new URLSearchParams(window.location.href.split('?')[1])
  )
  keyWord = keyWord?.trim?.()
  const [searchWord, setSearchWord] = React.useState(keyWord?.trim?.())
  const { appid, logo, bgSearch } = getPageConfig()
  const isVest = isVestAppid(appid)

  const handleBack = () => {
    if (history.length > 1) {
      history.goBack()
    } else {
      history.push('/')
    }
  }

  const goContact = () => {
    if (appid === 'cm1006') {
      tracking({
        _step_id: 3,
        _feedback_page_id: '',
        _postion_id: 1,
        _search_words: keyWord,
        _faq_id: '',
        _src_faq_id: ''
      })
    } else {
      reportFn({
        parm1: 3,
        parm3: 1,
        parm5: 'h5_feedback_click_2',
        parm9: keyWord
      })
    }
    history.push(`/ticketForm`)
  }

  // 马甲站：对照原型 search 屏（返回 + 搜索条，无 banner）
  if (isVest) {
    return (
      <div className={clsx(styles.container, styles.vest)}>
        <div className={styles.searchNav}>
          <button
            type='button'
            className={styles.searchBack}
            aria-label='Back'
            onClick={handleBack}
          >
            <img src={backIcon} alt='' />
          </button>
          <div className={styles.searchBarWrap}>
            <SearchInput
              clear={Boolean(searchWord)}
              value={searchWord}
              onChange={(value) => setSearchWord(value)}
              onSearch={(value) => {
                history.replace(
                  `/searchResult?${new URLSearchParams({
                    keyWord: value
                  }).toString()}`
                )
              }}
            />
          </div>
        </div>

        {keyWord ? (
          <SearchResultList keyWord={keyWord} isVest />
        ) : (
          <RelaterList keyWord={keyWord} />
        )}

        {keyWord ? null : (
          <div className={clsx(styles.tips, styles.vestTips)}>
            {getTextByKey('Still need help')}&nbsp;
            <a onClick={goContact}>{getTextByKey('Contact Us')}</a>
          </div>
        )}
      </div>
    )
  }

  return (
    <div className={clsx(styles.container, styles?.[appid])}>
      <header className={clsx(styles.header)}>
        <div
          className='bg-search'
          style={{ backgroundImage: `url(${bgSearch})` }}
        />
        <h2 className='title'>{getTextByKey('Search Result')}</h2>
        <Link to='/'>
          <div className='logo' style={{ backgroundImage: `url(${logo})` }} />
        </Link>
        <div className='search-wrap'>
          <SearchInput
            clear={Boolean(searchWord)}
            value={searchWord}
            onChange={(value) => setSearchWord(value)}
            onSearch={(value) => {
              history.replace(
                `/searchResult?${new URLSearchParams({
                  keyWord: value
                }).toString()}`
              )
            }}
          />
        </div>
        {['cm1006', 'cm1007']?.includes(appid) && (
          <div className='round-b'></div>
        )}
      </header>

      <SearchResultList keyWord={keyWord} />

      <RelaterList keyWord={keyWord} />

      <div className={clsx(styles.tips)} style={{ textAlign: 'center' }}>
        {getTextByKey('Still need help')}&nbsp;
        <a onClick={goContact}>{getTextByKey('Contact Us')}</a>
      </div>
    </div>
  )
}
export default SearchResult
